"use client";

import { Icon, OspToast, ospThemes } from "@ospweb/sdk-ui";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { cn } from "utils";
import log from "logbeacon";
import { parseUnits } from "viem";
import { PaymentRadio } from "../PaymentRadio";
import { PaymentContentProps } from "./types";
import { PaymentDetails } from "./PaymentDetails";
import { useConnect, useContract } from "@/src/hooks";

import { I18nUtil } from "@utils/i18n";
import { MemberProduct } from "@/src/features/api/memberApi";
import Translations from "@translate/components/Payment/PaymentContent/PaymentContent";

const I18n = I18nUtil.createScoped(Translations);

export const PaymentContent: React.FC<PaymentContentProps> = ({
  planId,
  isWaitingPay,
  className,
  data,
  plan,
  isTrial,
  onWallet,
  onSelectNetworkCallback,
}) => {
  const [isPaying, setIsPaying] = useState(isWaitingPay || false);
  const [selectedNetwork, setSelectedNetwork] = useState<any>(null);
  const {
    onSwitchChain,
    isConnected,
    onConnect,
    address,
    chainId,
    isConnecting,
  } = useConnect();
  const {
    allowance,
    isLoading,
    approveHash,
    isPending,
    approveError,
    isConfirming,
    isApprovedSuccess,
    handleInfiniteApprove,
    refetchAllowance,
  } = useContract(
    address as `0x${string}`,
    selectedNetwork?.address as `0x${string}`,
    Number(selectedNetwork?.chain_id as any) || 0,
  );
  const approvedRef = useRef<boolean>(false);

  const isApproved = useMemo(() => {
    log.info("[Payment] allowance", allowance, selectedNetwork?.price);
    return !!(
      allowance &&
      allowance >=
        parseUnits(selectedNetwork?.price || "0", selectedNetwork?.decimals)
    );
  }, [allowance, selectedNetwork]);
  const networkList = useMemo(() => {
    return plan?.currencies?.filter((currency) => {
      return currency.channel === "wallet";
    });
  }, [plan]);
  useEffect(() => {
    setIsPaying(isWaitingPay || false);
  }, [isWaitingPay]);

  const handleCryptoPayment = useCallback(async () => {
    if (!isApproved) {
      approvedRef.current = true;
      handleInfiniteApprove();
    } else {
      onWallet?.();
    }
  }, [isApproved, handleInfiniteApprove, onWallet]);

  const onPay = useCallback(async () => {
    log.info(`[Payment][trace][buy]`, planId);
    setIsPaying(true);
    if (Number(chainId) !== Number(selectedNetwork?.chain_id as any)) {
      const res = await onSwitchChain(Number(selectedNetwork?.chain_id as any));
      if (res) {
        await handleCryptoPayment();
      } else {
        setIsPaying(false);
      }
    } else {
      await handleCryptoPayment();
    }
  }, [handleCryptoPayment, chainId, selectedNetwork?.chain_id, onSwitchChain]);

  const amount = useMemo(() => {
    log.debug("[文案]selectedNetwork", selectedNetwork);
    return `${selectedNetwork?.price} ${selectedNetwork?.currency}`;
  }, [selectedNetwork?.price, selectedNetwork?.currency]);

  useEffect(() => {
    log.info(
      "[Payment]approve",
      approveHash,
      isApprovedSuccess,
      approvedRef.current,
      approveError,
    );
    if (approveHash && isApprovedSuccess && approvedRef.current) {
      refetchAllowance();
      onWallet?.();
      approvedRef.current = false;
    } else if (approveError && approvedRef.current) {
      setIsPaying(false);
      approvedRef.current = false;
      log.debug("[Payment] approve error", approveError);
      OspToast.error({
        content: I18n.t("User rejected the approve."),
      });
    }
  }, [approveHash, isApprovedSuccess, approveError, onWallet]);

  const getPayButtonText = useCallback(() => {
    if (isConnected && isApproved) {
      return I18n.t("Pay now");
    } else if (isConnected && !isApproved) {
      return I18n.t("Approve and pay");
    } else {
      return I18n.t("Pay now");
    }
  }, [isApproved, isConnected]);

  const getPayButtonDisabled = useCallback(() => {
    if (isConnected) {
      return isPaying || isPending || isConfirming || isLoading;
    } else {
      return isConnecting || !isConnected;
    }
  }, [isPaying, isPending, isConfirming, isLoading, isConnecting, isConnected]);

  // Memoize the network change handler
  const handleNetworkChange = useCallback(
    (network: any) => {
      setSelectedNetwork(network);
      onSelectNetworkCallback?.(network);
      if (isConnected) {
        refetchAllowance();
      }
    },
    [isConnected, onSelectNetworkCallback, refetchAllowance],
  );

  const paymentDetails = (
    <PaymentDetails
      amount={amount}
      isTrial={isTrial}
      plan={plan as MemberProduct}
      planId={planId}
      data={data}
    />
  );

  return (
    <div
      className={cn(
        "flex flex-col items-center md:flex-row md:items-start gap-6 mt-6 md:w-[740px] w-full",
        className,
      )}
    >
      <div className="flex md:hidden flex-col w-full justify-center items-center">
        {/* 支付详情 */}
        {paymentDetails}
      </div>
      <div className="flex flex-col gap-3 w-full flex-1">
        <PaymentRadio
          className="w-full max-w-md"
          onNetworkChange={handleNetworkChange}
          networkList={networkList as any}
          address={address as `0x${string}`}
          isConnected={isConnected}
          onConnect={onConnect}
          isConnecting={isConnecting}
          disabled={isPaying}
        />
      </div>
      <div className="flex flex-col w-full flex-1">
        {/* 支付详情 */}
        <div className="md:block hidden w-full">{paymentDetails}</div>
        <div className="flex flex-col flex-1 fixed md:relative bottom-5 max-md:left-4 max-md:right-4">
          {/* 服务条款 */}
          <p className="text-content_tertiary body_s md:mt-8 mt-1">
            {I18n.t("· Clicking 'Pay now' confirms your agreement to our")}
            <a
              href="https://edwealth.ai/terms_of_service.html"
              target="_blank"
              className="text-content_secondary"
            >
              {` ${I18n.t("Terms of Service")} `}
            </a>
            {` ${I18n.t("and")} `}
            <a
              href="https://edwealth.ai/privacy_policy.html"
              target="_blank"
              className="text-content_secondary"
            >
              {I18n.t("Privacy Policy")}
            </a>
          </p>
          {/* 支付按钮 */}
          <button
            type="button"
            data-openreplay-label={`PaymentContent · Button | ${getPayButtonText()}`}
            className={cn(
              "button md_button primary_button button_m group w-full md:mt-8 mt-1",
            )}
            onClick={onPay}
            disabled={getPayButtonDisabled()}
            data-loading={isPaying || isPending || isConfirming || isLoading}
          >
            <span className="group-loading:hidden">{getPayButtonText()}</span>
            <Icon
              name="ic_loading2"
              size={20}
              color={ospThemes.colors.content_inverse_primary}
              className={cn("hidden animate-spin group-loading:block")}
            />
          </button>
        </div>
      </div>
    </div>
  );
};
